import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  Grid,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VideocamIcon from "@mui/icons-material/Videocam";
import { getInterviews } from "../services/api";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const ProctoringAnalysis = () => {
  const location = useLocation();
  const requestRef = useRef(0);
  const [selectedUserId, setSelectedUserId] = useState(location.state?.selectedUserId || "");
  const skipDefaultDateFilter = Boolean(location.state?.skipDefaultDateFilter);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  useEffect(() => { setSelectedUserId(location.state?.selectedUserId || ""); }, [location.key]);

  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const fetchInterviews = async () => {
    const requestId = ++requestRef.current;
    try {
      setLoading(true);
      setError(null);
      const response = await getInterviews(null,null,"desc",null,skipDefaultDateFilter,selectedUserId || undefined);
      if (requestId !== requestRef.current) return;
      const interviewsArray = response.data || [];
      const proctoringAlertsInterviews = interviewsArray.filter(
        (interview) => interview.proctoringAlert === true,
      );
      setInterviews(proctoringAlertsInterviews);
      setLoading(false);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setError(err.message || "Failed to fetch interviews");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, [selectedUserId, skipDefaultDateFilter]);

  const handleViewVideos = (interviewId) => {
    const videoUrl = `${window.location.origin}/interview-video/${interviewId}`;
    window.open(videoUrl, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <Container
        maxWidth={false}
        sx={{ mt: 8, display: "flex", justifyContent: "center", py: 8 }}
      >
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 8, textAlign: "center", py: 8 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {selectedUserId && <Box sx={{display:"flex",alignItems:"center",gap:1,py:1}}><Typography variant="body2">Selected team / user</Typography><Button size="small" onClick={() => setSelectedUserId("")}>Show all users</Button></Box>}
      {/* Sticky Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
          borderRadius: 3,
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Grid item>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.5px",
                  lineHeight: 1.2,
                }}
              >
                Proctoring Analysis
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  ml: 0,
                  pl: 0,
                  lineHeight: 1.4,
                  alignSelf: "flex-start",
                }}
              >
                View interviews with proctoring alerts
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/dashboard")}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Back to Overview
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Scrollable Table Section */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.04)"
              : "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(10px)",
        }}
      >
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ tableLayout: "auto" }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "var(--bg-page)",
                  position: "sticky",
                  top: 0,
                  zIndex: 9,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    py: 2,
                    px: { xs: 1, sm: 2 },
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                >
                  Candidate
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    py: 2,
                    px: { xs: 1, sm: 2 },
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                >
                  Job Opening / Req Id
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    py: 2,
                    px: { xs: 1, sm: 2 },
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                  align="center"
                >
                  Duration
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    py: 2,
                    px: { xs: 1, sm: 2 },
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                  align="center"
                >
                  Date / Time
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    py: 2,
                    px: { xs: 1, sm: 2 },
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                  align="center"
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {interviews.length > 0 ? (
                interviews.map((interview) => (
                  <TableRow
                    key={interview._id}
                    hover
                    sx={{
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.05)"
                            : "#f1f5f9",
                      },
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <TableCell
                      sx={{ whiteSpace: "nowrap", py: 2, px: { xs: 1, sm: 2 } }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {interview.candidateName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {interview.candidateEmail}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mt: 1,
                        }}
                      >
                        <Chip
                          icon={<WarningIcon sx={{ color: "#ef4444" }} />}
                          label="Proctoring Alert"
                          sx={{
                            backgroundColor: "#fef2f2",
                            color: "#dc2626",
                            fontWeight: 600,
                            borderRadius: "8px",
                          }}
                        />
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{ whiteSpace: "nowrap", py: 2, px: { xs: 1, sm: 2 } }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {interview.jobTemplate?.name || "-"}
                      </Typography>
                      {interview.jobTemplate?.reqId && (
                        <Chip
                          label={`Req Id: ${interview.jobTemplate.reqId}`}
                          size="small"
                          sx={{
                            mt: 1,
                            backgroundColor: "#ede9fe",
                            color: "#6d28d9",
                            fontWeight: 600,
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell
                      sx={{ whiteSpace: "nowrap", py: 2, px: { xs: 1, sm: 2 } }}
                      align="center"
                    >
                      {interview.interviewDuration ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(59, 130, 246, 0.18)"
                                : "rgba(59, 130, 246, 0.1)",
                            border: "1px solid rgba(59, 130, 246, 0.3)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.88rem",
                              color: "#3B82F6",
                              lineHeight: 1,
                            }}
                          >
                            {interview.interviewDuration} min
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell
                      sx={{ whiteSpace: "nowrap", py: 2, px: { xs: 1, sm: 2 } }}
                      align="center"
                    >
                      <Stack spacing={0.8} alignItems="center">
                        {interview.interviewDate &&
                        parseLocalDate(interview.interviewDate) ? (
                          <Chip
                            label={format(
                              parseLocalDate(interview.interviewDate),
                              "dd MMM yyyy",
                            )}
                            sx={{
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(15, 118, 110, 0.3)"
                                  : "#E6F7F4",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#6EE7B7"
                                  : "#0F766E",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                            }}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Invalid Date
                          </Typography>
                        )}

                        {interview.interviewTime ? (
                          <Chip
                            label={format(
                              new Date(`1970-01-01T${interview.interviewTime}`),
                              "hh:mm a",
                            )}
                            sx={{
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.1)"
                                  : "rgba(0, 0, 0, 0.06)",
                              color: "text.primary",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                            }}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell
                      sx={{ whiteSpace: "nowrap", py: 2, px: { xs: 1, sm: 2 } }}
                      align="center"
                    >
                      <Tooltip title="View Interview Videos">
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<VideocamIcon />}
                          onClick={() => handleViewVideos(interview._id)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: "none",
                          }}
                        >
                          View Report
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{ textAlign: "center", py: 8, color: "text.secondary" }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No interviews with proctoring alerts found.
                    </Typography>
                    <Typography variant="body2">
                      When an interview has proctoring alerts, it will appear
                      here.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default ProctoringAnalysis;
